import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import {
  ArrowLeft,
  ArrowRight,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  Check,
  Copy,
} from "lucide-react";
import { getPostBySlug, getAdjacentPosts } from "@/api/post";
import { getImageUrl } from "@/api/upload";
import { useNewsletterSubscribe } from "@/hooks/email/useNewsletterSubscribe";
import SEO from "@/components/SEO";
import { getCanonicalUrl, getSharePreviewUrlForPath } from "@/config/seo.config";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import TurnstileWidget from "@/components/shared/TurnstileWidget";
import NotFound from "@/pages/NotFound";
import { toast } from "sonner";

// Importing the assets
import logoImg from "@/assets/logo.webp";
import blogsCtaBanner from "@/assets/blogs-cta-banner.webp";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [adjacentPosts, setAdjacentPosts] = useState({ previous: null, next: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const isTurnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

  const { mutate: subscribeNewsletter, isPending } = useNewsletterSubscribe();

  const hasFetchedRef = useRef(false);
  const currentSlugRef = useRef(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (currentSlugRef.current !== slug) {
      hasFetchedRef.current = false;
      currentSlugRef.current = slug;
    }

    if (hasFetchedRef.current) return;

    const fetchPost = async () => {
      try {
        setIsLoading(true);
        setError(null);
        hasFetchedRef.current = true;

        const response = await getPostBySlug(slug);
        setPost(response.data);

        const adjacentResponse = await getAdjacentPosts(slug);
        setAdjacentPosts(adjacentResponse.data);
      } catch (err) {
        console.error("Failed to fetch blog post:", err);
        setError(err.message);
        hasFetchedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const canonicalPostUrl = post?.slug
    ? getCanonicalUrl(`/resources/${post.slug}`)
    : typeof window !== "undefined"
      ? window.location.href
      : "";
  const socialShareUrl = post?.slug
    ? getSharePreviewUrlForPath(`/resources/${post.slug}`)
    : canonicalPostUrl;
  const shareTitle = post?.title || "Check out this blog post";
  const shareText = post?.excerpt || "Interesting article from VetAssist";

  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(socialShareUrl)}&via=vetassist`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(socialShareUrl)}`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(socialShareUrl)}`;
  const emailShareUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\nRead more: ${canonicalPostUrl}`)}`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: socialShareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error sharing:", err);
        }
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(socialShareUrl || canonicalPostUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) return;

    if (isTurnstileEnabled && !turnstileToken) {
      toast.error("Captcha verification failed. Please try again.");
      return;
    }

    subscribeNewsletter(
      {
        email: normalizedEmail,
        source: "Blog post CTA",
        turnstileToken,
      },
      {
        onSuccess: () => {
          setEmail("");
          setTurnstileToken("");
          setTurnstileResetKey((prev) => prev + 1);
          turnstileRef.current?.reset?.();
        },
      }
    );
  };

  // Reusable background component for both loading and main state
  const AnimatedBackground = () => (
    <HomeAuroraBackground
      showStars={false}
      showWavingLines={true}
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    />
  );

  const processContentImages = (htmlContent) => {
    if (!htmlContent) return "";
    return htmlContent.replace(
      /<img\s+([^>]*?\s+)?src="([^"]+)"([^>]*)>/gi,
      (match, before = "", src, after = "") => {
        if (src.startsWith("http://") || src.startsWith("https://")) return match;
        const fullUrl = getImageUrl(src);
        return `<img ${before}src="${fullUrl}"${after}>`;
      }
    );
  };

  if (isLoading) {
    return (
      <div
        className="relative min-h-screen w-full font-sans text-slate-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <AnimatedBackground />
        <NavigationHeader />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-lg text-slate-700">Loading post...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <>
        <SEO
          page="resources"
          title="Post Not Found | Vet Assist Blog"
          description="The requested blog post could not be found."
          noindex={true}
          canonical={getCanonicalUrl("/resources")}
        />
        <NotFound />
      </>
    );
  }

  const formattedDate = new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      className="relative min-h-screen w-full font-sans text-slate-900 selection:bg-blue-500/30 selection:text-blue-900"
      style={{
        background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
      }}>
      <SEO
        page="resources"
        title={`${post.title} | Vet Assist Blog`}
        description={post.excerpt || post.title}
        image={post.featured_image ? getImageUrl(post.featured_image) : null}
        imageAlt={post.featured_image_alt || post.title}
        type="article"
        article={{
          title: post.title,
          excerpt: post.excerpt,
          featured_image: post.featured_image ? getImageUrl(post.featured_image) : null,
          slug: post.slug,
          created_at: post.published_at || post.created_at,
          updated_at: post.updated_at,
          author_name: post.author_name || "Vet Assist Team",
          category_name: post.category_name,
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/resources" },
          { name: post.title, url: `/resources/${post.slug}` },
        ]}
      />

      <style>{`
        /* --- Prose Styles --- */
        .prose p, .prose span, .prose strong, .prose em, .prose b, .prose i, .prose u, .prose a {
          background-color: transparent !important;
        }

        .prose ul,
        .prose ol {
          margin-top: 1.25rem !important;
          margin-bottom: 1.25rem !important;
          padding-left: 1.5rem !important;
        }

        .prose ul {
          list-style-type: disc !important;
        }

        .prose ol {
          list-style-type: decimal !important;
        }

        .prose li {
          display: list-item !important;
          margin-top: 0.45rem !important;
          margin-bottom: 0.45rem !important;
        }

        .prose li::marker {
          color: #1A3A5C !important;
          font-weight: 600 !important;
        }

        .prose h2 {
          display: inline-block !important;
          border-bottom: 4px solid #3b82f6 !important;
          padding-bottom: 0.25rem !important;
          margin-top: 2.5rem !important;
          margin-bottom: 1.25rem !important;
          color: #1A3A5C !important;
          font-weight: bold !important;
          font-size: 1.5rem !important;
        }
        
        @media (min-width: 768px) {
          .prose h2 {
            font-size: 2.25rem !important;
            margin-top: 3rem !important;
            margin-bottom: 1.5rem !important;
          }
        }

        .prose h1, .prose h3, .prose h4, .prose h5, .prose h6 {
          border-bottom: none !important;
        }

        .prose img {
          border-radius: 1rem !important;
          box-shadow: 0 4px 20px -5px rgb(0 0 0 / 0.5) !important;
          margin-top: 2rem !important;
          margin-bottom: 2rem !important;
          width: 100% !important;
          height: auto !important;
          display: block !important;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        @media (min-width: 768px) {
          .prose img {
            border-radius: 1.5rem !important;
            margin-top: 3rem !important;
            margin-bottom: 3rem !important;
          }
        }

        @media (min-width: 1024px) {
          .desktop-mask {
            mask-image: linear-gradient(to right, transparent 0%, black 30%, black 100%);
            -webkit-mask-image: linear-gradient(to right, transparent 0%, black 30%, black 100%);
          }
        }
      `}</style>

      <AnimatedBackground />
      <NavigationHeader />

      {/* --- HERO SECTION --- */}
      <section className="mx-auto w-full max-w-450 px-4 pt-24 pb-8 md:px-8 md:pt-32 md:pb-12">
        <div className="mx-auto mb-8 max-w-5xl px-0 lg:px-0">
          <Link
            to="/resources"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-400 transition-colors hover:text-blue-400 md:text-base">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to all blogs</span>
          </Link>
        </div>

        <div className="mx-auto mb-8 max-w-4xl px-0 text-center md:mb-12">
          <div className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[10px] font-bold tracking-widest text-slate-400 uppercase md:mb-5 md:gap-x-4 md:text-xs">
            <span className="text-blue-300">{post.category_name || "Blog"}</span>
            <span className="h-1 w-1 rounded-full bg-slate-600"></span>
            <span>{formattedDate}</span>
          </div>

          <h1
            className="mb-4 text-2xl leading-tight font-bold tracking-tight text-slate-900 md:mb-5 md:text-4xl lg:text-5xl"
            style={{ wordWrap: "break-word" }}>
            {post.title}
          </h1>
        </div>

        <div className="mx-auto mb-8 w-full max-w-5xl overflow-hidden rounded-xl border border-white/10 shadow-2xl md:mb-12 md:rounded-[2rem]">
          <img
            src={post.featured_image ? getImageUrl(post.featured_image) : logoImg}
            alt={post.title}
            className="block h-auto w-full"
          />
        </div>

        {post.excerpt && (
          <div className="mx-auto mb-8 max-w-4xl px-0 md:mb-12">
            <p className="text-center text-base leading-relaxed font-light text-slate-700 italic md:text-xl">
              {post.excerpt}
            </p>
          </div>
        )}
      </section>

      {/* --- MAIN CONTENT --- */}
      <section className="mx-auto w-full max-w-5xl px-4 pb-12 md:px-8 lg:px-16">
        {/* Floating Share Bar - Desktop Only */}
        <div className="fixed top-1/2 right-8 z-40 hidden -translate-y-1/2 flex-col gap-3 lg:flex">
          <div className="flex flex-col gap-3 rounded-2xl border border-[#93c5fd]/30 bg-white/70 p-3 shadow-2xl backdrop-blur-md">
            <button
              onClick={handleNativeShare}
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-blue-600 hover:text-white"
              title="Share">
              <Share2 className="h-5 w-5" />
            </button>
            <a
              href={twitterShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-sky-500 hover:text-white"
              title="Share on Twitter">
              <Twitter className="h-5 w-5" />
            </a>
            <a
              href={facebookShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-blue-600 hover:text-white"
              title="Share on Facebook">
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={linkedInShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-blue-800 hover:text-white"
              title="Share on LinkedIn">
              <Linkedin className="h-5 w-5" />
            </a>
            <a
              href={emailShareUrl}
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-slate-600 hover:text-white"
              title="Share via Email">
              <Mail className="h-5 w-5" />
            </a>
            <button
              onClick={handleCopyLink}
              className="group flex h-12 w-12 cursor-pointer items-center justify-center rounded-xl bg-white/70 text-[#1A3A5C] transition-all hover:bg-green-600 hover:text-white"
              title="Copy Link">
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div
          className="prose prose-base md:prose-lg prose-h1:mt-8 prose-h1:mb-4 prose-h1:text-2xl md:prose-h1:text-3xl prose-h1:font-bold prose-h1:text-[#1A3A5C] prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-lg md:prose-h3:text-xl prose-h3:font-bold prose-h3:text-[#1A3A5C] prose-p:leading-relaxed prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-[#1A3A5C] prose-a:text-blue-700 hover:prose-a:text-blue-800 prose-blockquote:rounded-2xl prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:p-6 md:prose-blockquote:p-8 prose-blockquote:text-[#1A3A5C] prose-blockquote:italic max-w-none"
          style={{ wordWrap: "break-word" }}
          dangerouslySetInnerHTML={{ __html: processContentImages(post.content) }}
        />
      </section>

      {/* --- SHARE SECTION --- */}
      <section className="border-t border-[#93c5fd]/30 bg-white/35 px-4 py-12 backdrop-blur-sm md:py-16">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col items-center justify-center text-center">
            <h3 className="mb-4 text-2xl font-bold text-[#1A3A5C] md:text-3xl">
              Found this helpful? Share it!
            </h3>
            <p className="mb-8 max-w-2xl text-base text-slate-700 md:text-lg">
              Help others discover this article by sharing it on your favorite platform.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {typeof navigator !== "undefined" && navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="flex cursor-pointer items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-700 hover:shadow-lg">
                  <Share2 className="h-5 w-5" />
                  <span>Share</span>
                </button>
              )}
              <a
                href={twitterShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex cursor-pointer items-center gap-2 rounded-full border border-[#93c5fd]/35 bg-white/70 px-6 py-3 font-semibold text-[#1A3A5C] transition-all hover:border-sky-500 hover:bg-sky-500 hover:text-white">
                <Twitter className="h-5 w-5" />
                <span className="hidden sm:inline">Twitter</span>
              </a>
              <a
                href={facebookShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex cursor-pointer items-center gap-2 rounded-full border border-[#93c5fd]/35 bg-white/70 px-6 py-3 font-semibold text-[#1A3A5C] transition-all hover:border-blue-600 hover:bg-blue-600 hover:text-white">
                <Facebook className="h-5 w-5" />
                <span className="hidden sm:inline">Facebook</span>
              </a>
              <a
                href={linkedInShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex cursor-pointer items-center gap-2 rounded-full border border-[#93c5fd]/35 bg-white/70 px-6 py-3 font-semibold text-[#1A3A5C] transition-all hover:border-blue-800 hover:bg-blue-800 hover:text-white">
                <Linkedin className="h-5 w-5" />
                <span className="hidden sm:inline">LinkedIn</span>
              </a>
              <a
                href={emailShareUrl}
                className="flex cursor-pointer items-center gap-2 rounded-full border border-[#93c5fd]/35 bg-white/70 px-6 py-3 font-semibold text-[#1A3A5C] transition-all hover:border-slate-600 hover:bg-slate-600 hover:text-white">
                <Mail className="h-5 w-5" />
                <span className="hidden sm:inline">Email</span>
              </a>
              <button
                onClick={handleCopyLink}
                className="flex cursor-pointer items-center gap-2 rounded-full border border-[#93c5fd]/35 bg-white/70 px-6 py-3 font-semibold text-[#1A3A5C] transition-all hover:border-emerald-600 hover:bg-emerald-600 hover:text-white">
                {copied ? (
                  <>
                    <Check className="h-5 w-5" />
                    <span className="hidden sm:inline">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    <span className="hidden sm:inline">Copy Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --- CONTINUE READING SECTION --- */}
      <section className="border-t border-[#93c5fd]/30 bg-transparent px-4 py-12 md:py-16">
        <div className="mx-auto max-w-5xl">
          <h3 className="mb-8 text-center text-2xl font-bold text-[#1A3A5C] md:mb-12 md:text-3xl">
            Continue Reading
          </h3>

          {!adjacentPosts.previous && !adjacentPosts.next && (
            <div className="py-8 text-center">
              <p className="text-slate-400">No more posts available.</p>
            </div>
          )}

          <div className="grid gap-8 md:grid-cols-2 md:gap-12">
            {/* Previous Post */}
            {adjacentPosts.previous ? (
              <Link
                to={`/resources/${adjacentPosts.previous.slug}`}
                className="group cursor-pointer rounded-2xl border border-[#93c5fd]/20 bg-white/55 p-4 backdrop-blur-sm transition-all hover:border-[#93c5fd]/40 hover:bg-white/70">
                <div className="mb-4 flex items-center gap-2 text-sm font-bold tracking-wider text-slate-400 uppercase">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Previous</span>
                </div>
                <div className="mb-4 aspect-video overflow-hidden rounded-xl shadow-sm md:mb-6 md:rounded-2xl">
                  <img
                    src={
                      adjacentPosts.previous.featured_image
                        ? getImageUrl(adjacentPosts.previous.featured_image)
                        : logoImg
                    }
                    alt={adjacentPosts.previous.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="mb-2 flex gap-3 text-[10px] font-bold tracking-wider text-blue-400 uppercase md:mb-3 md:text-xs">
                  <span>{adjacentPosts.previous.category_name || "Blog"}</span>
                </div>
                <h4
                  className="mb-2 text-xl leading-tight font-bold text-[#1A3A5C] transition-colors group-hover:text-blue-700 md:mb-3 md:text-2xl"
                  style={{ wordWrap: "break-word" }}>
                  {adjacentPosts.previous.title}
                </h4>
                {adjacentPosts.previous.excerpt && (
                  <p
                    className="line-clamp-2 text-sm leading-relaxed text-slate-700 md:text-base"
                    style={{ wordWrap: "break-word" }}>
                    {adjacentPosts.previous.excerpt}
                  </p>
                )}
              </Link>
            ) : (
              <div className="flex items-center justify-center rounded-2xl border border-[#93c5fd]/20 bg-white/55 p-8 backdrop-blur-sm">
                <p className="text-center text-slate-500">No previous post</p>
              </div>
            )}

            {/* Next Post */}
            {adjacentPosts.next ? (
              <Link
                to={`/resources/${adjacentPosts.next.slug}`}
                className="group cursor-pointer rounded-2xl border border-[#93c5fd]/20 bg-white/55 p-4 backdrop-blur-sm transition-all hover:border-[#93c5fd]/40 hover:bg-white/70">
                <div className="mb-4 flex items-center justify-end gap-2 text-sm font-bold tracking-wider text-slate-400 uppercase">
                  <span>Next</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
                <div className="mb-4 aspect-video overflow-hidden rounded-xl shadow-sm md:mb-6 md:rounded-2xl">
                  <img
                    src={
                      adjacentPosts.next.featured_image
                        ? getImageUrl(adjacentPosts.next.featured_image)
                        : logoImg
                    }
                    alt={adjacentPosts.next.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="mb-2 flex justify-end gap-3 text-[10px] font-bold tracking-wider text-blue-400 uppercase md:mb-3 md:text-xs">
                  <span>{adjacentPosts.next.category_name || "Blog"}</span>
                </div>
                <h4
                  className="mb-2 text-right text-xl leading-tight font-bold text-[#1A3A5C] transition-colors group-hover:text-blue-700 md:mb-3 md:text-2xl"
                  style={{ wordWrap: "break-word" }}>
                  {adjacentPosts.next.title}
                </h4>
                {adjacentPosts.next.excerpt && (
                  <p
                    className="line-clamp-2 text-right text-sm leading-relaxed text-slate-700 md:text-base"
                    style={{ wordWrap: "break-word" }}>
                    {adjacentPosts.next.excerpt}
                  </p>
                )}
              </Link>
            ) : (
              <div className="flex items-center justify-center rounded-2xl border border-[#93c5fd]/20 bg-white/55 p-8 backdrop-blur-sm">
                <p className="text-center text-slate-500">No next post</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* --- SUBSCRIBE CTA SECTION --- */}
      <section className="relative flex w-full flex-col items-center justify-center overflow-hidden border-t border-white/10 py-16 lg:flex-row lg:justify-end lg:py-20">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${blogsCtaBanner})`,
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="desktop-mask absolute inset-0 z-10 bg-[#0f172a]/80 backdrop-blur-md lg:right-0 lg:left-auto lg:w-[65%] lg:backdrop-blur-lg" />

        <div className="relative z-20 mx-auto flex w-full max-w-7xl flex-col px-6 lg:items-end lg:px-12 lg:text-right">
          <div className="flex w-full flex-col items-center text-center lg:w-[55%] lg:items-end lg:text-right">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 shadow-lg backdrop-blur-md md:mb-8">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]"></span>
              <span className="text-[10px] font-bold tracking-wide text-gray-100 uppercase md:text-xs">
                Join 500+ subscribers
              </span>
            </div>

            <h2 className="mb-4 text-3xl leading-[1.1] font-extrabold tracking-tight text-white drop-shadow-xl md:mb-6 md:text-5xl lg:text-6xl">
              Want to stay ahead in the <br className="hidden lg:block" />
              <span className="text-blue-400 drop-shadow-[0_0_15px_rgba(96,165,250,0.4)]">
                veterinary world?
              </span>
            </h2>

            <p className="mb-8 max-w-lg text-base leading-relaxed font-medium text-slate-300 drop-shadow-md md:mb-10 md:text-xl">
              Subscribe for the latest news, trends, and expert advice delivered straight to your
              inbox.
            </p>

            <div className="w-full max-w-lg">
              <form onSubmit={handleSubscribe} className="flex w-full flex-col gap-3">
                <div className="flex w-full flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 rounded-xl border border-white/20 bg-white/5 px-6 py-4 text-white placeholder-slate-400 shadow-inner backdrop-blur-sm transition-all outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/40"
                    required
                    disabled={isPending}
                  />
                  <button
                    type="submit"
                    disabled={isPending || (isTurnstileEnabled && !turnstileToken)}
                    className="group flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-8 py-4 font-bold whitespace-nowrap text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-[0_0_20px_rgba(16,185,129,0.5)] disabled:cursor-not-allowed disabled:opacity-50">
                    <span>{isPending ? "Subscribing..." : "Subscribe"}</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>

                {isTurnstileEnabled && (
                  <div className="flex flex-col items-start gap-3">
                    <TurnstileWidget
                      ref={turnstileRef}
                      className="w-full text-left"
                      action="newsletter_subscribe"
                      mode="managed"
                      resetKey={turnstileResetKey}
                      onTokenChange={setTurnstileToken}
                      onError={(message) => toast.error(message)}
                    />
                  </div>
                )}
              </form>
            </div>

            <p className="mt-6 text-xs font-medium text-slate-400">
              No spam, unsubscribe anytime. Read our{" "}
              <Link
                to="/privacy"
                className="text-blue-400 transition-colors hover:text-blue-300 hover:underline">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;
